/**
 * fm-scroll-nav.js
 * Dynamic scroll-spy navigation for the sidebar layout.
 *
 * - Auto-discovers sections with [data-scroll-section] attribute
 * - Builds nav links from data-scroll-label values
 * - Uses IntersectionObserver to highlight active section
 * - Smooth scrolls on click
 * - Mobile: controls sidebar visibility + auto-scrolls nav to active link
 */

(function () {
  'use strict';

  const NAV_SELECTOR = '[data-fm-scroll-nav]';
  const SECTION_SELECTOR = '[data-scroll-section]';
  const SIDEBAR_SELECTOR = '[data-fm-sidebar]';
  const ACTIVE_CLASS = 'is-active';
  const VISIBLE_CLASS = 'is-visible';
  const LG_BREAKPOINT = 990;

  // Root margin: trigger when section is in the upper-middle portion of viewport
  const OBSERVER_ROOT_MARGIN = '-25% 0% -50% 0%';

  let nav = null;
  let navList = null;
  let sidebar = null;
  let sections = [];
  let links = [];
  let observer = null;
  let firstSectionObserver = null;
  let activeLink = null;

  function init() {
    nav = document.querySelector(NAV_SELECTOR);
    if (!nav) return;

    navList = nav.querySelector('.fm-scroll-nav__list');
    if (!navList) return;

    sidebar = document.querySelector(SIDEBAR_SELECTOR);

    discoverSections();
    if (sections.length === 0) return;

    buildNavLinks();
    setupScrollObserver();
    setupMobileVisibility();
  }

  /**
   * Find all sections with data-scroll-section attribute
   */
  function discoverSections() {
    sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
  }

  /**
   * Build nav links from discovered sections
   */
  function buildNavLinks() {
    navList.innerHTML = '';
    links = [];

    sections.forEach(function (section) {
      var label = section.getAttribute('data-scroll-label');
      if (!label) return;

      var sectionId = section.getAttribute('data-scroll-section');
      var link = document.createElement('a');
      link.className = 'fm-scroll-nav__link';
      link.textContent = label;
      link.setAttribute('href', '#section-' + sectionId);
      link.dataset.target = sectionId;

      link.addEventListener('click', function (e) {
        e.preventDefault();
        setActiveLink(sectionId);
        smoothScrollTo(section);
      });

      navList.appendChild(link);
      links.push({ el: link, section: section, id: sectionId });
    });
  }

  /**
   * Smooth scroll to a section, accounting for header + mobile nav height
   */
  function smoothScrollTo(section) {
    var headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height'),
      10
    ) || 60;

    var navHeight = 0;
    if (window.innerWidth < LG_BREAKPOINT && sidebar) {
      navHeight = sidebar.offsetHeight;
    }

    var offset = headerHeight + navHeight + 8;
    var top = section.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: top,
      behavior: 'smooth',
    });
  }

  /**
   * IntersectionObserver to detect which section is currently in view
   */
  function setupScrollObserver() {
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var sectionId = entry.target.getAttribute('data-scroll-section');
          setActiveLink(sectionId);
        });
      },
      {
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: 0,
      }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  /**
   * Set the active nav link
   */
  function setActiveLink(sectionId) {
    links.forEach(function (item) {
      if (item.id === sectionId) {
        item.el.classList.add(ACTIVE_CLASS);
        item.el.setAttribute('aria-current', 'true');
        activeLink = item.el;
      } else {
        item.el.classList.remove(ACTIVE_CLASS);
        item.el.removeAttribute('aria-current');
      }
    });

    // Mobile: auto-scroll nav to keep active link visible
    if (window.innerWidth < LG_BREAKPOINT && activeLink) {
      scrollNavToActive();
    }
  }

  /**
   * On mobile, scroll the horizontal nav to center the active link
   */
  function scrollNavToActive() {
    if (!activeLink || !navList) return;

    var linkLeft = activeLink.offsetLeft;
    var linkWidth = activeLink.offsetWidth;
    var navWidth = navList.offsetWidth;
    var scrollTarget = linkLeft - navWidth / 2 + linkWidth / 2;

    navList.scrollTo({
      left: scrollTarget,
      behavior: 'smooth',
    });
  }

  /**
   * Mobile: show/hide the sidebar when first scroll section enters/exits viewport
   */
  function setupMobileVisibility() {
    if (!sidebar || sections.length === 0) return;

    // On desktop, sidebar is always visible via CSS (sticky)
    // On mobile, we show it when the first data-scroll-section is near the viewport

    var firstSection = sections[0];

    firstSectionObserver = new IntersectionObserver(
      function (entries) {
        if (window.innerWidth >= LG_BREAKPOINT) return;

        entries.forEach(function (entry) {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
            sidebar.classList.add(VISIBLE_CLASS);
          } else if (entry.boundingClientRect.top > 0) {
            sidebar.classList.remove(VISIBLE_CLASS);
          }
        });
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      }
    );

    firstSectionObserver.observe(firstSection);

    // Also check on scroll for edge cases (user scrolled past first section)
    var scrollCheck = debounceScroll(function () {
      if (window.innerWidth >= LG_BREAKPOINT) return;

      var rect = firstSection.getBoundingClientRect();
      var headerHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height'),
        10
      ) || 60;

      if (rect.top <= headerHeight + 100) {
        sidebar.classList.add(VISIBLE_CLASS);
      } else {
        sidebar.classList.remove(VISIBLE_CLASS);
      }
    }, 100);

    window.addEventListener('scroll', scrollCheck, { passive: true });
  }

  /**
   * Simple debounce for scroll handler
   */
  function debounceScroll(fn, wait) {
    var timeout;
    return function () {
      clearTimeout(timeout);
      timeout = setTimeout(fn, wait);
    };
  }

  // Re-init on Shopify section events (Theme Editor)
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 100);
  });

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
